/**
 * @file THE CLIP — how many rounds are in the gun, how many are in the pocket,
 * and the 1.6 seconds in between. THE MODEL ONLY.
 *
 * Before this file there was one number and it was infinite: the auto-attack
 * fired every `FIRE.interval` for ever, and the only thing that could stop it
 * was death. This is the state machine that gives the trigger something to run
 * out of — a clip, a reserve, a reload clock and a safety catch — and nothing
 * else. It does not pick targets, it does not roll misses, it does not know
 * what a bullet is or how far one flies.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS, NO ENGINE, NO GAME
 * ─────────────────────────────────────────────────────────────────────────────
 * Zero imports. The `Wallet.js` / `Shops.js` / `VehicleDamage.js` mould: the
 * table (CONTENT, `src/game/weapons.js`) and the bus are INJECTED, both may be
 * `null`, and every branch below is provable in node with no GPU and no
 * browser. `table: null, bus: null` is a legal, fully-stepping weapon system
 * that simply cannot fire — which is what lets `tests/weapons.test.mjs` drive
 * the whole clip machine with duck types.
 *
 * It also draws no randomness at all — rule 4 is satisfied by having nothing to
 * satisfy. There is no `Math.random()` here and no seeded stream either: a clip
 * count is arithmetic. The miss roll lives in `systems/accuracy.js`, where the
 * `'aim'` stream already is.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ IT IS NOT GIVEN THE `Player`, AND THAT IS THE POINT
 * ─────────────────────────────────────────────────────────────────────────────
 * The moment this file reads `player.dead` it owns a SECOND gate on whether the
 * gun may fire, and that gate can disagree with the one `Gameplay` already has
 * (`!player.dead && !driving?.inCar`). Two gates on one action is precisely the
 * `hurtPlayer(amount, lethal)` family of bug this project has already paid for
 * once — two callers, two readings of "may this happen", and the disagreement
 * only visible in the one state nobody tests. So: `Gameplay` decides whether a
 * shot is allowed and TELLS this module what happened. This module decides
 * whether the gun has a round for it. One question each.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIVE THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **`update(dt)` IS CALLED UNCONDITIONALLY, EVERY STEP, AND `canFire()` IS THE
 *   ONLY GATE.** A reload is a CLOCK, not an action. A clock parked behind the
 *   same condition that gates firing gives you "my gun never reloaded because I
 *   walked through a door" — the reload freezes in a car, in a shop, on a
 *   cutscene, and comes back a minute later still at 1.5 s remaining. Hence the
 *   asymmetry, and it is deliberate: **you can reload in a car; you cannot shoot
 *   from one.**
 *
 * • **NOTHING INTERRUPTS A RELOAD.** Not incoming fire, not going on safe, not
 *   opening a shop panel. A reload cancelled by damage sounds tactical and is
 *   unplayable under an AUTO-attack the player does not aim: every hostile in
 *   range is shooting, so the cancel fires continuously and the gun never
 *   refills — an unrecoverable loop that looks like a bug rather than a
 *   mechanic. {@link createWeapons.onRespawn} is the only thing that clears a
 *   running reload.
 *
 * • **EMPTY RESERVE IS TERMINAL AND DOES NOT LOOP.** With `clip 0` and
 *   `reserve 0`: `canFire()` is false, `reload()` is false, `reloadT` stays
 *   EXACTLY 0 across any number of steps, and nothing further is published. A
 *   reload that restarts against an empty reserve is an infinite emit loop
 *   pumping the HUD sixty times a second for ever. The way out is
 *   {@link createWeapons.addAmmo} or {@link createWeapons.onRespawn}, both of
 *   which are events somebody else caused.
 *
 * • **SAFE/HOT DOES NOT TOUCH THE FIRE CADENCE.** The `hot` test lives INSIDE
 *   `canFire()`, so `Gameplay`'s accumulator keeps consuming intervals while the
 *   player is on safe and going hot **does not unload a banked burst**. That is
 *   the exact trap `EnemyManager._stepFire` documents ("banks at most ONE shot"
 *   — a unit that spent a minute out of range would empty a magazine the instant
 *   it closed); it is solved here by never creating it. Suppressing the
 *   accumulator instead would reintroduce it.
 *
 * • ⚠ **`interval` NEVER RETURNS 0.** The caller's cadence is
 *   `while (acc >= interval) { acc -= interval; fire(); }` — a zero interval is
 *   an infinite loop that hangs the tab, and it is reachable from CONTENT (a
 *   malformed def, or no table at all). The getter returns `Infinity` in both
 *   cases, which makes the loop body unreachable instead.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THIS FILE IS THE SOLE PUBLISHER OF `'player:ammo'`
 * ─────────────────────────────────────────────────────────────────────────────
 * `Gameplay.js` must never emit on that channel, and `tests/weapons.test.mjs`
 * section (h) greps its source to prove it. Two reasons, and both have to hold:
 *
 *   1. **A PURCHASE HAPPENS WHILE THE FIXED STEP IS FROZEN.** An open shop panel
 *      sets the time scale to 0 — measured 0 `Level#update` calls/s open against
 *      102 closed (`docs/shops.md`) — so a step-end emitter in `Gameplay` would
 *      simply never fire for the one event the player is looking straight at.
 *      That is the bug `'shop:state'`'s synchronous republish already exists to
 *      avoid. Every mutator here publishes SYNCHRONOUSLY, inside the call.
 *   2. **TWO PUBLISHERS ON ONE CHANNEL FLICKER.** The `'hud:prompt'` rule: when
 *      two systems both own a channel, the HUD shows whichever spoke last and
 *      the disagreement is a flicker nobody can reproduce.
 *
 * The channel carries the WHOLE state every time (see {@link publish}) rather
 * than a delta, so a subscriber that missed an event is still correct on the
 * next one, and the HUD keeps no copy of the clip. It fires ONCE ON
 * CONSTRUCTION, like `Wallet.js` — a HUD that renders `null` until its first
 * event would sit blank until the player's first shot.
 *
 * ⚠ An idle frame emits NOTHING (the edge gate). Reload progress rides a
 * {@link RELOAD_TICK} sub-tick, ~16 emits per reload, PLUS both edges always.
 * A CSS animation keyed on a sequence number was considered and REJECTED: it
 * keeps animating while the sim is frozen under a shop panel, so the readout
 * would lie in exactly the state you opened the panel to look at —
 * `/play/shopui`'s status line shipped that way once and read `open 0` over a
 * live open panel. A progress bar driven by real events cannot do that: when
 * the sim stops, the bar stops.
 *
 * @module engine/systems/Weapons
 */

/**
 * ⚠ DECLARED HERE, NOT IMPORTED FROM THE CONTENT FILE. `src/game/weapons.js`
 * owns the authored roster and its own `WeaponDef` typedef, but a
 * `@typedef {import('../../game/weapons.js')...}` here would be the only
 * reference to `src/game/` anywhere under `src/engine/` — no other engine file
 * has one, and the next reader to grep for the boundary would find it and file
 * it as a breach. This is the SHAPE the model consumes; the content file is
 * free to carry fields this one never reads.
 *
 * @typedef {Object} WeaponDef
 * @property {string} id @property {string} label
 * @property {number} damage @property {number} interval
 * @property {number} clipSize @property {number} reloadTime
 * @property {number} reserveMax @property {number} startReserve
 * @property {number} ammoPerBox
 */

/**
 * Seconds of reload per progress emit. 0.1 against a 1.6 s reload is ~16 emits
 * — enough that a progress bar moves smoothly at any frame rate, few enough
 * that the channel is not a per-frame firehose. Both EDGES are published
 * regardless, so the bar always starts at 0 and always lands on 1: the sub-tick
 * only decides how many steps it takes in between.
 */
export const RELOAD_TICK = 0.1;

/**
 * @param {any} n
 * @returns {boolean} A real, usable number. Same guard as `Wallet.js#usable` —
 *   a `NaN` from a malformed def must not poison the arithmetic.
 */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * @param {any} v
 * @param {number} min
 * @returns {number} `v` as a whole number >= `min`, or `min`.
 */
const whole = (v, min) => (usable(v) && v >= min ? Math.floor(v) : min);

/**
 * The player's gun: one clip, one reserve, one reload clock, one safety catch.
 *
 * @param {Object} [o]
 * @param {{starting?: string, weapons?: readonly WeaponDef[]}|null} [o.table] The
 *   CONTENT table, injected — `src/game/weapons.js#createWeaponTable()`. Null is
 *   legal: nothing resolves, `canFire()` is permanently false and the system
 *   still steps. `Gameplay` should simply not construct one in that case; the
 *   guard is defensive.
 * @param {any} [o.bus] `'player:ammo'` — see the header. Null means no emits and
 *   no subscriptions; everything else works identically.
 * @param {string|null} [o.startId] Which weapon to hold. Defaults to the table's
 *   own `starting`.
 * @returns {Object}
 */
export function createWeapons(o = {}) {
  const { table = null, bus = null, startId = null } = o ?? {};

  const defs = Array.isArray(table?.weapons) ? table.weapons : [];
  const wantId = typeof startId === 'string' && startId
    ? startId
    : (typeof table?.starting === 'string' ? table.starting : null);
  /**
   * THE def, held by reference. The numbers are never copied out into locals —
   * one place a weapon's stats exist, so a second weapon is a lookup and not a
   * re-initialisation.
   * @type {WeaponDef|null}
   */
  const weapon = defs.find((d) => d && d.id === wantId) ?? null;
  if (!weapon && wantId !== null) {
    // A table WAS supplied and the id did not resolve: that is a content typo,
    // and a silent fallback to `weapons[0]` would hide it behind a gun that
    // works. Refusing to resolve is visible on the very first trigger pull.
    console.warn(`[weapons] no weapon with id '${wantId}' in the table — the gun is unarmed`);
  }

  // ── the numbers, read through the def and guarded once ────────────────────
  const clipSize = weapon ? whole(weapon.clipSize, 0) : 0;
  const reserveMax = weapon ? whole(weapon.reserveMax, 0) : 0;
  const reloadTime = weapon && usable(weapon.reloadTime) && weapon.reloadTime > 0 ? weapon.reloadTime : 0;

  // ── the state ─────────────────────────────────────────────────────────────
  /** Rounds in the gun. */
  let clip = clipSize;
  /** Rounds in the pocket. Never above {@link reserveMax}. */
  let reserve = weapon ? Math.min(reserveMax, whole(weapon.startReserve, 0)) : 0;
  /** Seconds REMAINING on the reload; 0 means not reloading. Never negative. */
  let reloadT = 0;
  /** The stance. True = the auto-attack may fire. */
  let hot = true;
  /** Which {@link RELOAD_TICK} bucket last published; -1 = none this reload. */
  let lastTick = -1;
  let disposed = false;

  // ── the counters (for `stats()`, `DebugPanel` and the suites) ─────────────
  /** Rounds that actually left the gun. */
  let shots = 0;
  /** Reloads COMPLETED — not started. A reload that is still running is `reloading`. */
  let reloads = 0;
  /** Trigger pulls refused because the clip was empty. NOT counted for a pull
   *  refused by the safety catch or by a running reload: those are the player
   *  and the clock doing their jobs, and only this one means "it clicked". */
  let dry = 0;
  /** Rounds ADDED to the reserve, lifetime. Named for its only real caller. */
  let bought = 0;

  /** @type {Array<() => void>} */
  const unsubs = [];

  /**
   * THE channel. The whole state, every time — see the header on why it is not
   * a delta and why nobody else may emit here.
   */
  const publish = () => {
    if (!bus) return;
    bus.emit?.('player:ammo', {
      id: weapon ? weapon.id : null,
      label: weapon ? weapon.label : null,
      clip,
      clipSize,
      reserve,
      reserveMax,
      reloading: reloadT > 0,
      reloadT,
      reloadTime,
      hot,
    });
  };

  /**
   * Moves rounds out of the reserve and into the clip, and stops the clock.
   * SILENT — publishing is always the public method's last act, so one call is
   * one event no matter how many private steps it took.
   */
  const _finish = () => {
    const take = Math.min(clipSize - clip, reserve);
    if (take > 0) {
      clip += take;
      reserve -= take;
    }
    reloadT = 0;
    lastTick = -1;
    reloads++;
  };

  /**
   * THE ONE PLACE A RELOAD STARTS. The manual `R` and the automatic
   * empty-the-clip both come through here, so the three refusals below cannot
   * be enforced twice and differently.
   *
   * ⚠ A zero (or absent) `reloadTime` is legal content — an instant reload —
   * and is resolved on the spot rather than by parking `reloadT` at 0, which
   * would leave the gun "reloading" for ever without a clock to finish it.
   *
   * SILENT: see {@link _finish}.
   * @returns {boolean} A reload is now running (or has already completed).
   */
  const _begin = () => {
    if (!weapon || reloadT > 0) return false; // already reloading
    if (clip >= clipSize) return false; // nothing to fill
    if (reserve <= 0) return false; // ⚠ terminal — see the header
    if (reloadTime <= 0) {
      _finish();
      return true;
    }
    reloadT = reloadTime;
    lastTick = -1;
    return true;
  };

  /** @returns {boolean} The gun will give up a round if asked right now. */
  const canFire = () => !disposed && hot && reloadT <= 0 && clip > 0;

  // The HUD's first paint depends on this — the `Wallet.js` rule.
  publish();

  const api = {
    name: 'weapons',

    // ── the questions ────────────────────────────────────────────────────────

    canFire,

    /**
     * @returns {boolean} The reserve has room for at least one more round. The
     *   shop asks this to decide whether an ammo row is buyable, so the offer
     *   and the purchase share one test and cannot disagree.
     */
    roomForAmmo() {
      return !disposed && !!weapon && reserve < reserveMax;
    },

    // ── the trigger ──────────────────────────────────────────────────────────

    /**
     * ONE ROUND LEAVES THE GUN.
     *
     * ⚠ Call this only when a round REALLY leaves — after the target has been
     * acquired, never before. A trigger pull at nothing costs no ammunition; a
     * MISS does, because a miss is a round that was fired. `Gameplay#fire()`
     * orders it that way and `tests/weapons.test.mjs` section (g) pins it.
     *
     * The clip going empty starts the reload ON THIS SAME STEP, not the next
     * one: the round that empties the clip is what triggers it, so there is no
     * frame in which the gun is empty, idle and not yet reloading.
     *
     * @returns {boolean} True if a round was spent. False changes NOTHING —
     *   no state, no emit (the `Wallet#spend` rule: a refusal is silent).
     */
    spendRound() {
      if (!canFire()) {
        if (!disposed && weapon && hot && reloadT <= 0 && clip <= 0) dry++;
        return false;
      }
      clip--;
      shots++;
      if (clip <= 0) _begin();
      publish();
      return true;
    },

    /**
     * Starts a reload — the `R` key, and `'weapon:reload'` off the bus.
     *
     * ⚠ **IT TOPS UP.** Rounds already in the clip are KEPT, not discarded. The
     * realistic rule (drop the partial clip) generates one bug report per
     * player — "why did I lose 4 bullets" — and buys nothing this game does
     * anything with, since there is no clip inventory to track.
     *
     * @returns {boolean} A reload is now running. False when it is already
     *   reloading, when the clip is already full, or when the reserve is empty;
     *   a refusal changes nothing and emits nothing.
     */
    reload() {
      if (disposed || !_begin()) return false;
      publish();
      return true;
    },

    // ── resupply ─────────────────────────────────────────────────────────────

    /**
     * Rounds into the pocket — the shop, and anything else that ever hands the
     * player ammunition.
     *
     * ⚠ Publishes SYNCHRONOUSLY, inside this call, because the caller may be a
     * shop panel with the fixed step frozen at 0 (see the header). A refresh
     * deferred to the next `update` would arrive never.
     *
     * ⚠ AND IT MAY START A RELOAD. If the clip is empty and nothing is running,
     * buying ammunition arms the gun. Without that line the dry player who just
     * spent their last dollar on a box of rounds stands there with a dead
     * weapon: there is no manual trigger in this game, so nothing they can press
     * would ever consume the ammo they just bought. Only when the clip is
     * EMPTY — a part-full clip is still a working gun, and forcing a 1.6 s
     * reload onto somebody the instant they close a shop is worse than nothing.
     *
     * @param {number} n Rounds offered. A non-finite or non-positive count is
     *   refused rather than treated as a subtraction.
     * @returns {number} Rounds ACTUALLY added — 0 when the reserve was already
     *   at its ceiling, which is silent and changes nothing.
     */
    addAmmo(n) {
      if (disposed || !weapon || !usable(n) || n <= 0) return 0;
      const add = Math.min(reserveMax - reserve, Math.floor(n));
      if (add <= 0) return 0;
      reserve += add;
      bought += add;
      if (clip <= 0 && reloadT <= 0) _begin();
      publish();
      return add;
    },

    // ── the stance ───────────────────────────────────────────────────────────

    /**
     * Safe or hot. See the header: this gates `canFire()` and NOTHING else, so
     * the caller's cadence accumulator keeps running and going hot does not
     * unload a banked burst.
     * @param {boolean} b
     * @returns {boolean} The stance now. A no-op call emits nothing.
     */
    setHot(b) {
      const next = !!b;
      if (disposed || next === hot) return hot;
      hot = next;
      publish();
      return hot;
    },

    /** @returns {boolean} The stance now. */
    toggleHot() {
      return api.setHot(!hot);
    },

    // ── the clock ────────────────────────────────────────────────────────────

    /**
     * One fixed step. ⚠ CALL IT UNCONDITIONALLY — see the header: a reload is a
     * clock, and a clock gated on whether the player may shoot stops in a car,
     * in a shop and under a cutscene.
     *
     * An idle step (nothing reloading) does no work and emits nothing, which is
     * what makes "600 idle steps, zero bus traffic" true.
     * @param {number} dt Seconds.
     */
    update(dt) {
      if (disposed || !usable(dt) || dt <= 0 || reloadT <= 0) return;
      reloadT -= dt;
      if (reloadT <= 0) {
        _finish();
        publish(); // the far EDGE, always
        return;
      }
      // Progress: quantised, so the rate of this channel does not depend on the
      // frame rate. Both edges are published elsewhere; this is only the middle.
      const tick = Math.floor((reloadTime - reloadT) / RELOAD_TICK);
      if (tick !== lastTick) {
        lastTick = tick;
        publish();
      }
    },

    // ── the events somebody else causes ──────────────────────────────────────

    /**
     * The player woke up. Full clip, no reload running, safety off.
     *
     * ⚠ **THE CLIP IS A FREE REFILL AND THE RESERVE IS NOT TOUCHED — A RESPAWN
     * IS A RESET, NOT A RESUPPLY.** This method used to floor the reserve at
     * the def's `respawnFloor`, a documented STOPGAP against the dry-and-broke
     * dead end (out of ammo AND out of money, with income at missions only, is
     * a state no amount of play recovers from). That field was **DELETED on
     * 2026-08-13** when the resupply drone landed — `systems/Drone.js` fixes
     * that dead end at the source, for free, while the player is still alive
     * and can see it happen, so a reserve that quietly refilled on death was
     * papering over a hole that no longer exists. A dry player now wakes with
     * ONE clip and the drone brings the rest.
     *
     * This is the one publish besides construction that is unconditional: the
     * HUD is coming back from a death splash and must repaint whatever the
     * numbers say.
     */
    onRespawn() {
      if (disposed || !weapon) return;
      clip = clipSize;
      reloadT = 0;
      lastTick = -1;
      hot = true;
      publish();
    },

    // ── readouts ─────────────────────────────────────────────────────────────

    /** @returns {string|null} The held weapon's id, or null if none resolved. */
    get id() {
      return weapon ? weapon.id : null;
    },
    /** @returns {string|null} What the HUD prints. */
    get label() {
      return weapon ? weapon.label : null;
    },
    /**
     * @returns {number} Seconds between rounds. ⚠ NEVER 0 — see the header; the
     *   caller's `while (acc >= interval)` would never terminate.
     */
    get interval() {
      return weapon && usable(weapon.interval) && weapon.interval > 0 ? weapon.interval : Infinity;
    },
    /** @returns {number} Hit points per round that connects. 0 with no weapon. */
    get damage() {
      return weapon && usable(weapon.damage) ? weapon.damage : 0;
    },
    /** @returns {number} Rounds in the gun. */
    get clip() {
      return clip;
    },
    /** @returns {number} Rounds in a full clip. */
    get clipSize() {
      return clipSize;
    },
    /** @returns {number} Rounds in the pocket. */
    get reserve() {
      return reserve;
    },
    /** @returns {number} The pocket's ceiling. */
    get reserveMax() {
      return reserveMax;
    },
    /**
     * @returns {number} Rounds in one box of ammunition, per the def. 0 when no
     *   weapon resolved or the field is missing/malformed.
     *
     * ⚠ **THIS GETTER IS WHAT FINALLY MAKES `ammoPerBox` A FIELD SOMETHING
     * READS.** Until 2026-08-13 it was authored, asserted (a box is a whole
     * number of clips: 60 / 12 = 5, no orphan rounds) and otherwise INERT — a
     * mirrored dead constant sitting beside the gunsmith row's `amount: 60`
     * with nothing tying the two together, which is the
     * `links.js`/`countryroads.js` trap in miniature and is `HANDOFF.md` open
     * item 6. `systems/Drone.js` derives its delivery payload from this getter,
     * so the box the weapon is authored to take is now the box that arrives.
     *
     * ⚠ The shop's `SHOP_CATALOGUE.gunsmith.ammo` `amount: 60` is STILL a
     * second independent copy, and it is left that way DELIBERATELY:
     * `engine/systems/Shops.js` may not import `src/game/`, so the catalogue
     * cannot read a weapon def and a shared constant would have to live in the
     * engine, which is the wrong side of the seam for content. What HAS changed
     * is that the mirror can no longer drift silently — `tests/weapons.test.mjs`
     * now carries a cross-file assertion pinning the two numbers to each other,
     * so moving one and forgetting the other fails a suite instead of quietly
     * selling a different box from the one the drone delivers.
     */
    get ammoPerBox() {
      return weapon && usable(weapon.ammoPerBox) && weapon.ammoPerBox > 0 ? Math.floor(weapon.ammoPerBox) : 0;
    },
    /** @returns {boolean} A reload is running. */
    get reloading() {
      return reloadT > 0;
    },
    /** @returns {number} Seconds REMAINING on it; 0 when not reloading. */
    get reloadT() {
      return reloadT;
    },
    /** @returns {number} Seconds a whole reload takes. */
    get reloadTime() {
      return reloadTime;
    },
    /** @returns {boolean} The stance. */
    get hot() {
      return hot;
    },

    /**
     * @returns {{id: string|null, clip: number, clipSize: number, reserve: number,
     *   reserveMax: number, reloading: boolean, reloadT: number, hot: boolean,
     *   shots: number, reloads: number, dry: number, bought: number}} A plain
     *   object for `Gameplay.stats()` and `DebugPanel`. ⚠ There is no `range`
     *   here and there is no `range` anywhere in this module — the gun's reach
     *   is `Gameplay#FIRE.range`, derived from the CAST. See the long note in
     *   `src/game/weapons.js`.
     */
    stats() {
      return {
        id: weapon ? weapon.id : null,
        clip,
        clipSize,
        reserve,
        reserveMax,
        reloading: reloadT > 0,
        reloadT,
        hot,
        shots,
        reloads,
        dry,
        bought,
      };
    },

    /**
     * Idempotent. Drops the bus subscriptions and freezes the figures: every
     * mutator is a no-op afterwards and nothing more is published, so a system
     * torn down mid-frame cannot repaint a HUD that is on its way out. The
     * `Wallet#dispose` rule.
     */
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const off of unsubs) {
        try {
          off();
        } catch {
          /* a bus that tore itself down first is not our problem */
        }
      }
      unsubs.length = 0;
    },
  };

  // ── the reverse channels ───────────────────────────────────────────────────
  // TWO DOORS, ONE METHOD — the `shops.press()` vs `'shop:buy'` shape. `Gameplay`
  // calls `reload()`/`toggleHot()` for the keyboard; the HUD's tap targets come
  // through here, so a touch player has the same gun as a keyboard player and
  // neither path can grow its own rules.
  if (bus?.on) {
    const onReload = () => api.reload();
    const onHot = () => api.toggleHot();
    // `createBus().on` returns its own unsubscriber; the `off(type, fn)`
    // fallback is for a duck-typed bus in a suite that does not.
    const offReload = bus.on('weapon:reload', onReload);
    const offHot = bus.on('weapon:hot', onHot);
    unsubs.push(typeof offReload === 'function' ? offReload : () => bus.off?.('weapon:reload', onReload));
    unsubs.push(typeof offHot === 'function' ? offHot : () => bus.off?.('weapon:hot', onHot));
  }

  return api;
}
